import { WidgetState } from '@/components/Widget/types';
import { transformImage } from '@/model/algolia/utils';
import { ArrayType } from '@/types/common';
import {
  ExpectedTierType,
  JackpotProviders,
  JackpotProvidersUnion,
  JackpotSlotMapped,
  JackpotTile,
  JackpotV2ValueInterface,
  JackpotsV2StateValues,
  MultipleJackpotV2Interface,
} from '@/types/jackpotTypes';

export const getJackpotPropertiesFromJackpotTile = ({
  jackpotTile,
  multiJackpots,
  currency,
  locale,
}: {
  jackpotTile?: JackpotTile;
  multiJackpots?: Record<string, MultipleJackpotV2Interface>;
  currency?: string;
  locale?: string;
}): JackpotSlotMapped | undefined => {
  if (!jackpotTile || !multiJackpots) return;
  const jackpotData = multiJackpots[jackpotTile.code];
  if (!jackpotData || !jackpotData.values) return;

  const mapper = getJackpotTileMapper(jackpotTile.provider);
  const tiers = jackpotTile.fields.map(mapper(jackpotData.values));

  const timestamp = new Date(jackpotData.timestamp || Date.now()).getTime();
  return buildJackpotSlots(jackpotTile, tiers, timestamp, currency, locale);
};

const getJackpotTileMapper = (provider: JackpotProvidersUnion) => {
  if (provider === JackpotProviders.RedTiger) {
    return tileMapper(redTigerStateMapper, {
      name: '',
      curValue: 0,
      state: 'normal',
    });
  }
  if (provider === JackpotProviders.BluePrint) {
    return tileMapper(
      state => (state.toLowerCase() || 'active') as WidgetState
    );
  }
  return tileMapper(state => state.toLowerCase() as WidgetState);
};

const tileMapper = (
  mapState: (state: JackpotsV2StateValues, index: number) => WidgetState,
  defaultValue?: ExpectedTierType
) => (jpValuesPerCode: Record<string, JackpotV2ValueInterface>) => (
  jpField: ArrayType<JackpotTile['fields']>,
  index: number
): ExpectedTierType => {
  if (!jpValuesPerCode[jpField.code] && defaultValue) return defaultValue;
  const jpValue = jpValuesPerCode[jpField.code] || { price: 0, state: '' };
  return {
    name: jpField.title || '',
    curValue: jpValue.price,
    state: mapState(jpValue.state, index),
    releaseMessage: jpField.messages?.active,
    gradientClass: jpField.gradientClass,
    previousPrice: jpValue.previousPrice,
    ...(jpValue.targetTime && {
      releaseTime: new Date(jpValue.targetTime).toString(),
    }),
    type: jpValue?.type,
  };
};

const redTigerStateMapper = (
  jackpotState: JackpotsV2StateValues,
  index: number
) => {
  if (jackpotState === JackpotsV2StateValues.WON) {
    return 'won';
  }
  return index === 0 ? 'normal' : 'active';
};

const buildJackpotSlots = (
  jackpotTile: JackpotTile,
  tiers: ExpectedTierType[],
  timestamp: number,
  currency?: string,
  locale?: string
) => {
  const wonTier = tiers.find(tier => tier.state === 'won');
  return {
    type: jackpotTile.provider,
    staticTexts: jackpotTile.staticTexts,
    timestamp,
    images: {
      background: {
        source: getImageSource(
          wonTier ? jackpotTile.background.won : jackpotTile.background.normal
        ),
      },
      logo: {
        source: getImageSource(jackpotTile.logo.source),
        alt: jackpotTile.logo.alt || '',
      },
    },
    currencyCode: currency,
    locale,
    visibleTiersAmount: jackpotTile.visibleFieldsAmount,
    tiers: tiers,
  };
};

const getImageSource = (src: string | undefined) =>
  src ? transformImage(src) : { normal: '' };
